import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

function sampleRateFromEnv(v, fallback) {
  const n = Number(v);
  if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
  return fallback;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN?.trim();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    enabled: process.env.NODE_ENV === 'production',
    integrations: [
      // React Router v6/v7-style integration (best available for SPA navigation naming).
      // If this ever becomes incompatible with your router version, fall back to Sentry.browserTracingIntegration().
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        matchRoutes,
        createRoutesFromChildren,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: sampleRateFromEnv(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    tracePropagationTargets: ['localhost'],
    replaysSessionSampleRate: sampleRateFromEnv(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0),
    replaysOnErrorSampleRate: sampleRateFromEnv(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 0.2),
  });
}

export function captureFeedbackEvent({ category, message, email, includeTechDetails, extra = {} }) {
  const eventId = Sentry.captureMessage(`Web feedback: ${category || 'general'}`, {
    level: 'info',
    tags: {
      feedback: 'web-form',
      category: category || 'general',
    },
    extra: {
      message,
      email: email || undefined,
      includeTechDetails: Boolean(includeTechDetails),
      ...(includeTechDetails
        ? {
            path: window.location.pathname,
            href: window.location.href,
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
          }
        : {}),
      ...extra,
    },
  });

  return eventId;
}

export function openSentryReportDialog(eventId) {
  const dsn = process.env.REACT_APP_SENTRY_DSN?.trim();
  if (!dsn) return;
  if (!eventId) return;
  Sentry.showReportDialog({ eventId });
}

